
import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import FocusPointGridArrow from "@/components/Reusable/CustomInputs/FocusPointInput/FocusPointGridArrow.vue";
import Icon from "@/components/UI/Icon.vue";

type MousePosition = {
  x: number;
  y: number;
};

@Component({
  components: { FocusPointGridArrow, Icon }
})
export default class FocusPointGrid extends Vue {
  @Prop()
  value!: { x: number; y: number } | null;

  get positions() {
    return [
      {
        name: "top-left",
        x: 0,
        y: 0
      },
      {
        name: "top-center",
        x: 50,
        y: 0
      },
      {
        name: "top-right",
        x: 100,
        y: 0
      },
      {
        name: "center-left",
        x: 0,
        y: 50
      },
      {
        name: "center-center",
        x: 50,
        y: 50
      },
      {
        name: "center-right",
        x: 100,
        y: 50
      },
      {
        name: "bottom-left",
        x: 0,
        y: 100
      },
      {
        name: "bottom-center",
        x: 50,
        y: 100
      },
      {
        name: "bottom-right",
        x: 100,
        y: 100
      }
    ];
  }

  isPositionSelected(position: { x: number; y: number; name: string }) {
    if (!this.value) return position.name === "center-center";
    return position.x === this.value.x && position.y === this.value.y;
  }

  dragStart(e: MouseEvent) {
    e.preventDefault();

    document.addEventListener("mousemove", this.onDrag);
    document.addEventListener("mouseup", this.dragEnd);
  }

  // euclidean distance
  distance(pointA: MousePosition, pointB: MousePosition) {
    return Math.sqrt(
      Math.pow(pointA.x - pointB.x, 2) + Math.pow(pointA.y - pointB.y, 2)
    );
  }

  getClosestPositionFromMouse({ x, y }: MousePosition) {
    const element = document.elementFromPoint(x, y) as HTMLElement;

    return element.dataset.index !== undefined
      ? Number(element.dataset.index)
      : undefined;
  }

  getClosestPositionFromMouseEuclidean(mousePosition: MousePosition) {
    return (this.$refs.positions as HTMLElement[])
      .map(element => (element as HTMLElement).getBoundingClientRect())
      .map((rect, index) => {
        return { x: rect.x, y: rect.y, index };
      })
      .reduce((a, b) => {
        return this.distance(mousePosition, a) < this.distance(mousePosition, b)
          ? a
          : b;
      }).index;
  }

  onDrag(e: MouseEvent) {
    e.preventDefault();

    const mousePosition = { x: e.clientX, y: e.clientY };
    let foundIndex = this.getClosestPositionFromMouse(mousePosition);

    if (foundIndex === undefined) {
      // More expensive search in case the mouse is not over the grid.
      foundIndex = this.getClosestPositionFromMouseEuclidean(mousePosition);
    }

    this.$emit("input", this.positions[foundIndex]);
  }

  dragEnd() {
    document.removeEventListener("mousemove", this.onDrag);
    document.removeEventListener("mouseup", this.dragEnd);
  }
}
